import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import Draggable from 'react-draggable';

import Modal from 'antd/es/modal';
import Spin from 'antd/es/spin';
import Slider from 'antd/es/slider';

import colors from "config/colors";
import { Collapse } from "antd";
import CameraViewService from "services/cameraView";
import { genericDataColumns } from "views/Process/helpers";

import { Row, Col } from "components/layout";
import Button from "components/Button";
import { Text } from "components/Text";
import LazyImg from "components/LazyImg";
import Table from "components/Table";
import { dateMonthDayFormat, timeWithSecondsFormat } from "config/common";
import { formatDate } from "helpers/date";
const cameraViewService = new CameraViewService();
const { Panel } = Collapse;
const useIsMouned = () => {
  const ref = useRef(false);

  useLayoutEffect(() => {
    ref.current = true;
  }, []);

  return ref.current;
};

const CameraView = ({ doorId, startTs, endTs, tzOffset, mode, imgProps = {}, autoplay = false, logs=[] }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [current, setCurrent] = useState(-1);
  const [hasImages, setHasImages] = useState(true);
  const [isPlaying, setIsPlaying] = useState(autoplay);

  const sliderRef = useRef();
  const isMounted = useIsMouned();
  const columns = genericDataColumns(logs);

  const currImg = useMemo(() => {
    const currentImage = current !== -1 ? images[current] : null;
    const { url = null, ts = 0 } = currentImage || {};

    return {
      hasPrev: current > 0,
      hasNext: current < images.length - 1,
      src: url,
      timestamp: ts,
    };
  }, [current, images]);

  const currLog = useMemo(() => {
    if (logs.length > 0 && currImg.timestamp !== 0) {
      console.log(currImg.timestamp, logs)
      const currentLog = logs.find((obj) => obj.ts === currImg.timestamp);
      return [currentLog];
    } else return null;
  }, [currImg, logs]);

  useEffect(() => {
    if (doorId && startTs && endTs) {
      setIsLoading(true);
      cameraViewService.getImages({ door_id: doorId, from_ts: startTs, to_ts: endTs })
        .then(data => {
          if (!data) {
            setHasImages(false);
            return;
          }
          if (mode === 'debug') {
            data = data.map(image => {
              return {
                ...image,
                url: image.url.replace('/images', '/images-debug'),
              };
            });
          }
          setImages(data);
          setHasImages(data.length > 0);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [doorId, startTs, endTs, mode]);

  useEffect(() => {
    if (!isLoading && isPlaying) {
      const interval = setInterval(() => {
        setCurrent(c => {
          if (c < images.length - 1) {
            return c + 1;
          } else {
            setIsPlaying(false);
            return c;
          }
        });
      }, 500);

      return () => {
        clearInterval(interval);
      };
    }
  }, [isLoading, isPlaying, images]);

  useEffect(() => {
    setCurrent(images.length - 1);
  }, [isLoading, images]);

  useEffect(() => {
    if (sliderRef.current && isMounted) {
      sliderRef.current.focus();
    }
  }, [currImg, sliderRef, isMounted]);

  return (
    <Row>
      <Col span={24}>
        {currLog?.length>0 && (
          <Collapse style={{marginBottom:4}}>
            <Panel header="Logs" key="1">
              <StyledTable
                size="small"
                dataSource={currLog}
                columns={columns}
                rowKey="ts"
                pagination={false}
              />
            </Panel>
          </Collapse>
        )}
        <Row justify="center">
          {hasImages ? (
            <LazyImg
              width={'100%'}
              alt="Camera view not available"
              src={currImg.src}
              fallback={(
                <Spin
                  size={24}
                  style={{ width: '100%', padding: '36px' }}
                />
              )}
              {...imgProps}
            />
          ) : (
            <Text
              style={{
                fontSize: '14px',
                padding: '12px 0'
              }}
            >
              Camera view not available
            </Text>
          )}
        </Row>
        <Row style={{ paddingTop: '8px' }}>
          <Col span={24}>
            <CameraViewSlider
              ref={sliderRef}
              value={current}
              min={0}
              max={images.length - 1}
              tooltipVisible={false}
              onChange={pos => {
                setCurrent(pos);
              }}
            />
          </Col>
        </Row>
        <Row justify="space-between" align="middle">
          <Col>
            <Button shape="circle"
              disabled={!currImg.hasPrev}
              icon={{ name: 'chevrons-left', size: [16, 15], color: '#828C94' }}
              style={{ margin: '0 4px' }}
              onClick={(e) => {
                e.stopPropagation();
                setCurrent(0);
                setIsPlaying(false);
              }}
            />
            <Button shape="circle"
              disabled={!currImg.hasPrev}
              icon={{ name: 'chevron-left', size: [16, 15], color: '#828C94' }}
              style={{ margin: '0 4px' }}
              onClick={(e) => {
                e.stopPropagation();
                setCurrent(current - 1);
                setIsPlaying(false);
              }}
            />
            <Button shape="circle"
              icon={{
                name: isPlaying ? 'pause' : 'play',
                size: [16, 15],
                color: '#828C94',
                style: isPlaying ? {} : { paddingLeft: '4px' }
              }}
              style={{ margin: '0 4px' }}
              onClick={(e) => {
                e.stopPropagation();
                setIsPlaying(!isPlaying);
              }}
            />
          </Col>
          <Col>
            {currImg.timestamp ? (
              <Text style={{ fontSize: '14px', lineHeight: '22px', color: '#45494D' }}>
                {formatDate(currImg.timestamp * 1000, `${dateMonthDayFormat} 'at' ${timeWithSecondsFormat}`, { tzOffset })}
              </Text>
            ) : null}
          </Col>
          <Col>
            <Button shape="circle"
              disabled={!currImg.hasNext}
              icon={{ name: 'chevron-right', size: [16, 15], color: '#828C94' }}
              style={{ margin: '0 4px' }}
              onClick={(e) => {
                e.stopPropagation();
                setCurrent(current + 1);
                setIsPlaying(false);
              }}
            />
            <Button shape="circle"
              disabled={!currImg.hasNext}
              style={{ margin: '0 4px' }}
              icon={{ name: 'chevrons-right', size: [16, 15], color: '#828C94' }}
              onClick={(e) => {
                e.stopPropagation();
                setCurrent(images.length - 1);
                setIsPlaying(false);
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>

  );
};
const StyledTable = styled(Table)`
  & .ant-table {
    overflow: auto;
  }
`;

export const CameraViewModal = ({ show, onClose, ...rest }) => {
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
  const dragRef = useRef(null);

  return (
    <StyledCameraViewModal
      title="Camera View"
      visible={show}
      onCancel={() => {
        onClose && onClose();
      }}
      width={684}
      centered={true}
      footer={null}
      destroyOnClose={true}
      modalRender={modal => (
        <Draggable
          // disabled={dragDisabled}
          handle=".ant-modal-header"
          bounds={bounds}
          onStart={(event, uiData) => {
            const { clientWidth, clientHeight } = window.document.documentElement;
            const targetRect = dragRef.current?.getBoundingClientRect();
            if (!targetRect) {
              return;
            }
            setBounds({
              left: -targetRect.left + uiData.x,
              right: clientWidth - (targetRect.right - uiData.x),
              top: -targetRect.top + uiData.y,
              bottom: clientHeight - (targetRect.bottom - uiData.y),
            });
          }}
        >
          <div ref={dragRef}>{modal}</div>
        </Draggable>
      )}
    >
      <CameraView {...rest} />
    </StyledCameraViewModal>
  );
};

const StyledCameraViewModal = styled(Modal)`
  .ant-modal-header {
    cursor: move;
  }
`;

const CameraViewSlider = styled(Slider)`
  &:hover ..ant-slider-track {
    background-color: ${colors.primary};
  }

  & .ant-slider-track {
    background-color: ${colors.primary};
  }
`;

export default CameraView;